import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import {
	DropdownItem,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UtilService } from '@service/util/util.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { LoginSetupService } from '@service/login-setup/login-setup.service';
import { ProductsService } from '@service/products/products.service';
import { UserSettingsService } from '@service/userSettings/user-settings.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { SetPasswordComponent } from '@shared/set-password/set-password.component';
import { getCarrierLandingPages, LANDING_PAGES } from '@domain/constants/landing-pages';
import { environment } from '@environment/environment';
import { BaseComponent } from '@shared/base/base.component';
import { takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import {
	CapacitySearchSettings,
	DefaultCapacitySearchSettings,
} from '@domain/interface/capacity-search-settings';
import {
	CAPACITY_SEARCH_SETTINGS,
	getCapacitySearchTabs,
} from '@domain/constants/capacity-search-settings';
import { CapacitySearchSettingsService } from '@service/capacitySearchSettings/capacity-search-settings.service';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { IntegrationService } from '@service/integration/integration.service';
import { isPaidCarrierPlan } from '../../register/utils/carrier-utils';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import {
	COPY_TO_CLIPBOARD_MSGS,
	DASHBOARD_VERSION_OPTIONS,
	PROCUREMENT_VERSION_OPTIONS,
	USER_SETTINGS,
} from '@domain/constants/app-variables';


@Component({
	selector: 'app-profile-popover',
	templateUrl: './profile-popover.component.html',
	styleUrls: ['./profile-popover.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ProfilePopoverComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() showLogout: boolean = false;
	@Input() highlightLandingPageSection: boolean = false;

	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;

	user;
	profileForm: UntypedFormGroup;
	isProfileRequestInProgress: boolean = false;
	landingPages: DropdownItem[] = [];
	defaultCapacitySearchSettings: DefaultCapacitySearchSettings;
	hideEngageCarrierDialog: boolean;

	@ViewChild(MtxPopoverTrigger) profilePopover: MtxPopoverTrigger;

	capacitySearchSettings: CapacitySearchSettings = {
		pickupDateType: CAPACITY_SEARCH_SETTINGS.pickupDate,
		originDeadHead: 0,
		destinationDeadHead: 0,
		defaultMinimumTruckCount: 0,
		defaultMinimumFrequency: 0,
		defaultTrailerTypes: '',
		defaultCapacitySearchTab: getCapacitySearchTabs(this.productsService.TASKS).Notes.label,
		override: false,
	};

	profileOpenedFromDashboard: boolean = false;
	environment = environment;
	isAccountTypeCarrier: boolean = false;
	procurementVersionOptions = PROCUREMENT_VERSION_OPTIONS;
	dashboardVersionOptions = DASHBOARD_VERSION_OPTIONS;
	COPY_TO_CLIPBOARD_MSGS = COPY_TO_CLIPBOARD_MSGS;
	FROM_EMAIL_TOOLTIP =
		'Carrier and Booking Assistant emails are sent from this address. Replies route back to you.';

	constructor(
		private _formBuilder: UntypedFormBuilder,
		private restService: RestService,
		private urlService: UrlService,
		public cargochief: Cargochief,
		public utilService: UtilService,
		public dialog: MatDialog,
		private loginSetupService: LoginSetupService,
		private segmentWrapper: SegmentWrapperService,
		public productsService: ProductsService,
		public userSettings: UserSettingsService,
		public carrierVettingService: CarrierVettingService,
		private capacitySearchSettingsService: CapacitySearchSettingsService,
		public integrationService: IntegrationService,
		public accountSettingsService: AccountSettingsService
	) {
		super();
	}

	ngOnInit() {
		this.defaultCapacitySearchSettings = this.capacitySearchSettingsService.getSettings(
			CAPACITY_SEARCH_SETTINGS.settingType.userSetting
		);
		if (this.userSettings.HIDE_ENGAGE_CARRIER_DIALOG) {
			this.hideEngageCarrierDialog = false;
		}
		this.user = this.cargochief.getLoginSetupUser();
		if (IS_NOT_NULL_OR_UNDEFINED(this.cargochief.getUser())) {
			this.setProfileForm();
		}
		this.populateLandingPages();
		this.isAccountTypeCarrier = this.utilService.isAccountTypeCarrier();
	}

	setProfileForm() {
		this.profileForm = this._formBuilder.group({
			firstName: [this.user.firstName, Validators.required],
			lastName: [this.user.lastName, Validators.required],
			phoneNumber: [
				IS_NOT_EMPTY(this.user.phoneNumber) ? this.user.phoneNumber.split(' ext. ')[0] : '',
			],
			phoneNumberExt: [
				IS_NOT_EMPTY(this.user.phoneNumber) ? this.user.phoneNumber.split(' ext. ')[1] : '',
			],
			mobileNumber: [IS_NOT_EMPTY(this.user.mobileNumber) ? this.user.mobileNumber : ''],
			landingPage: [this.user.landingPage],
			showDashboard: [true],
			mcpUsername: [this.user.mcpUsername],
			onboardingLink: [this.user.onboardingLink],
			procurementVersionPreference: [this.userSettings.PROCUREMENT_VERSION_PREFERENCE],
			dashboardVersionPreference: [this.userSettings.DASHBOARD_VERSION_PREFERENCE],
		});
	}

	updateProfile() {
		this.isProfileRequestInProgress = true;
		const url = this.urlService.updateUser();
		let payload = Object.assign({}, this.profileForm.value);
		payload.userId = this.cargochief.USER_ID;
		payload.email = this.cargochief.getUser()['email'];
		payload.userType = this.cargochief.getUser()['userType'];

		// CN-8312: procurementVersionPreference is a user-setting, not a User
		// column. The /updateUser endpoint rejects unknown fields, so strip it
		// from the payload and persist via the dedicated /userSetting endpoint
		// after the profile save succeeds.
		const procurementVersionPreference = payload.procurementVersionPreference;
		delete payload.procurementVersionPreference;
		// CN-8712: dashboardVersionPreference is also a user-setting (not a User
		// column) — strip it from the /updateUser payload and persist via /userSetting.
		const dashboardVersionPreference = payload.dashboardVersionPreference;
		delete payload.dashboardVersionPreference;

		if (IS_NOT_EMPTY(payload.phoneNumberExt)) {
			payload.phoneNumber = payload.phoneNumber + ' ext. ' + payload.phoneNumberExt;
			delete payload.phoneNumberExt;
		}
		let request = {
			...payload,
			...this.capacitySearchSettings,
		};
		if (this.hideEngageCarrierDialog) {
			request['hideEngageCarrierDialog'] = false;
		}
		this.restService
			.POST(url, request)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					const onComplete = () => {
						this.utilService.showSnackbar(
							'Profile updated successfully.',
							SNACKBAR.SUCCESS,
							SNACKBAR.ACTION
						);
						this.loginSetupService.updateLoginSetup();
						this.isProfileRequestInProgress = false;
						// CN-8312: close the profile popover on a successful save (it
						// previously stayed open). Runs after both the plain and the
						// procurement-version-preference save paths.
						this.closeProfilePopover();
					};
					// CN-8312/CN-8712: user-setting preferences persist via the
					// dedicated /userSetting endpoint, separately from /updateUser.
					// Collect the ones that changed and save them together.
					const settingUpdateUrls: string[] = [];
					if (
						this.accountSettingsService.PROCUREMENT_VERSIONS &&
						IS_NOT_EMPTY(procurementVersionPreference) &&
						procurementVersionPreference !== this.userSettings.PROCUREMENT_VERSION_PREFERENCE
					) {
						settingUpdateUrls.push(
							this.urlService.updateUserSettingUrl({
								settingName: USER_SETTINGS.PROCUREMENT_VERSION_PREFERENCE,
								value: procurementVersionPreference,
							})
						);
					}
					if (
						this.accountSettingsService.DASHBOARD_VERSIONS &&
						IS_NOT_EMPTY(dashboardVersionPreference) &&
						dashboardVersionPreference !== this.userSettings.DASHBOARD_VERSION_PREFERENCE
					) {
						settingUpdateUrls.push(
							this.urlService.updateUserSettingUrl({
								settingName: USER_SETTINGS.DASHBOARD_VERSION_PREFERENCE,
								value: dashboardVersionPreference,
							})
						);
					}
					if (settingUpdateUrls.length) {
						// CN-8712: a failed preference write must not masquerade as success.
						// Surface the error and keep the popover open so the user can retry,
						// rather than silently leaving a partial-save state.
						const onSettingUpdateError = () => {
							this.utilService.showSnackbar(
								'Profile was updated, but one or more layout preferences could not be saved.',
								SNACKBAR.ERROR,
								SNACKBAR.ACTION
							);
							this.loginSetupService.updateLoginSetup();
							this.isProfileRequestInProgress = false;
						};
						forkJoin(
							settingUpdateUrls.map((settingUrl) => this.restService.PUT(settingUrl, {}))
						)
							.pipe(takeUntil(this.isDestroyed$))
							.subscribe(onComplete, onSettingUpdateError);
					} else {
						onComplete();
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						`Unable to update profile now. ${error.error}`,
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isProfileRequestInProgress = false;
				}
			);
	}

	openSetPasswordDialog() {
		const dialogRef = this.dialog.open(SetPasswordComponent, {
			width: '500px',
			data: {
				showCloseIcon: true,
			},
			disableClose: true,
		});
	}

	closeProfilePopover() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.profilePopover)) {
			this.profilePopover.closePopover();
		}
	}

	openProfilePopover() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.profilePopover && this.profilePopover.popoverClosed)) {
			this.profileOpenedFromDashboard = true;
			this.profilePopover.openPopover();
		}
	}

	populateLandingPages() {
		this.landingPages = [];
		let landingPages;
		if (this.utilService.isAccountTypeCarrier()) {
			const isPaidPlan = isPaidCarrierPlan(this.cargochief.PLAN);
			landingPages = getCarrierLandingPages(isPaidPlan);
		} else {
			landingPages = LANDING_PAGES;
		}
		Object.keys(landingPages)?.forEach((key) => {
			this.landingPages.push({
				label: landingPages[key].label,
				value: landingPages[key].value,
			});
		});
	}

	updateShowDashboardValue(value: boolean) {
		if (IS_NOT_NULL_OR_UNDEFINED(this.profileForm)) {
			this.profileForm.controls['showDashboard'].setValue(value);
		}
	}

	onCapacitySearchSettingsChange(payload: CapacitySearchSettings) {
		this.capacitySearchSettings = payload;
	}

	updateHideEngageCarrierDialogSetting() {
		if (this.userSettings?.HIDE_ENGAGE_CARRIER_DIALOG) {
			this.hideEngageCarrierDialog = true;
			this.updateProfile();
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
